import {
  ConnectedTable,
  Paginator,
  TableProvider,
  ConnectedTableLayout,
} from '@panwds/react-table-legacy'
import { Input, LoadingPage, Tooltip } from '@panwds/react-ui'
import { SearchIcon } from '@panwds/icons'
import { useCallback, useEffect, useMemo, useState } from 'react'
import styled from 'styled-components'
import { LoadLicense } from '../../../common/actions/GlobalActions'
import FadedLineHeader from '../../../common/components/fadedLineHeader/FadedLineHeader'

import TableHeader from '../../../common/components/table/TableHeader'
import MessageBanner from '../../../common/banner/MessageBanner'
import {
  setShowActionStatus
} from '../../../common/reducers/actions/ViewReducer.actions'
import {
  APPROVED_SUBSCRIPTION_LABEL, ASSOCIATED_TENANTS,
  CLAIMS_SUBSCRIPTION, PANORAMA_MULTI_CLOUD_USER_ALERT,
  READY_FOR_ACTIVATION
} from '../constants/SubscriptionConstant'
import ButtonRenderer from '../renders/ButtonRenderer'
import GroupByColumnRenderer from '../renders/GroupByColumnRenderer'
import LinkRenderer from '../renders/LinkRenderer'
import './subscription.scss'
import ContractRenderer from '../../../tenancy/components/licenses/cellRenderer/ContractRenderer'

const TableStyle = styled.div`
  div[role="row"] {
    padding: 4px;
    height: auto;
    background-color: transparent !important;
  }
  inset: 20 0 0 0;
  display: flex;
  flex-direction: column;
  height: calc(100% - 30px);
`

/*
 * Created By sswaminathan
 * Created on Mon Nov 22 2021
 * Created on SubscriptionTable.js
 * Copyright (c) 2021  ©Palo Alto Networks
 */

const SubscriptionTable = (props) => {
  const {
    pageSize,
    pageIndex,
    dispatch,
    onPaginationChange,
    tableColumnData,
    onCollapse,
    globalDispatch,
    isLoading,
    ...rest
  } = props
  const [updateLoading, setUpdateLoading] = useState(false)
  const [showError, setShowError] = useState(false)
  const [showErrorMsg, setShowErrorMsg] = useState('')
  const [tableData, setTableData] = useState()
  const [currentPageIndex, setCurrentPageIndex] = useState(pageIndex)
  const [currentPageSize] = useState(pageSize)
  const { licenseContext, licenseHash, activationFlow, licenseDispatch, globalTsgId } = rest

  useEffect(() => {
    setTableData([...tableColumnData])
  }, [tableColumnData])
  const toggleClick = useCallback((data) => {
    const newTableData = [...tableData]
    newTableData.forEach((each) => {
      if (each.id === data.id) {
        each.is_collapsed = !data.is_collapsed
      }
    })
    setTableData(newTableData)
  }, [tableData])

  const DataRenderer = useCallback(({ row, value }) => {
    let showSubRows = false
    if (row.isGrouped && row.leafRows?.length > 0) {
      showSubRows = !row.leafRows[0].original.is_collapsed
      return <ul className='tw-p-1 tw-w-full'>
        {
          row.leafRows.map((each, idx) => {
            return (each.original.is_primary || each.original.is_header || showSubRows) ?
              <GroupByColumnRenderer key={idx} {...{ row: each, value: each.original.product_desc, showSubRows, toggleClick }} /> : null
          })}
      </ul>
    }
  }, [toggleClick])

  const ValueDataRenderer = useCallback(({ row, value, name }) => {
    let showSubRows = false
    if (row.isGrouped && row.leafRows?.length > 0) {
      showSubRows = !row.leafRows[0].original.is_collapsed
      return <ul className='tw-p-1'>
        {
          row.leafRows.map((each, idx) => {
            let tenantInfo
            if (name === 'associated_tenant') {
              tenantInfo = each.original[name] ?? []
            }
            const displayValue = (name !== 'product_name' && name !== 'associated_tenant') ? each.original[name] :
              name === 'associated_tenant' ?
                <LinkRenderer flowType={ASSOCIATED_TENANTS} {...{ ...rest, value: tenantInfo, row: each, dispatch }} /> :
                <LinkRenderer {...{ row, value: each.original[name] }} />

            let cssClassName = ['product', 'contract', 'start_date', 'end_date'].includes(name) ?
              'tw-text-blue-600 tw-truncate' : 'tw-text-gray-900 dark:tw-text-gray-100 tw-truncate'
            cssClassName = each.original.is_header ? `${cssClassName} tw-pt-2` : cssClassName

            return (each.original.is_primary || each.original.is_header || showSubRows) ?
              <li key={idx} className={`tw-border-b-4  tw-border tw-relative tw-h-full tw-truncate tw-flex tw-flex-row ${cssClassName}`} style={{ minHeight: '3rem' }}>
                {displayValue}
              </li> : null
          })}
      </ul>
    }
  }, [dispatch, rest])
  const _ContractRenderer = useCallback(({ row, value, name }) => {
    let showSubRows = false
    if (row.isGrouped && row.leafRows?.length > 0) {
      showSubRows = !row.leafRows[0].original.is_collapsed
      return <ul className='tw-p-1'>
        {
          row.leafRows.map((each, idx) => {
            let cssClassName = ['product', 'contract', 'start_date', 'end_date'].includes(name) ?
              'tw-text-blue-600 tw-truncate' : 'tw-text-gray-900 tw-truncate'
            cssClassName = each.original.is_header ? `${cssClassName} tw-pt-2` : cssClassName

            return (each.original.is_primary || each.original.is_header || showSubRows) ?
              <li key={idx} className={`tw-border-b-4  tw-border tw-relative tw-h-full tw-truncate tw-flex tw-flex-row ${cssClassName}`} style={{ minHeight: '3rem' }}>
                <ContractRenderer row={{ original: each.original }} value={value} />
              </li> : null
          })}
      </ul>
    }
  }, [])
  /**
   *  Handles the message after the claim submission
   * @type {(function(*=, *=): void)|*}
   */
  const handleSubmit = useCallback((response, errorStatus) => {
    setShowError(errorStatus)
    if (!errorStatus) {
      LoadLicense({
        tsg_id: globalTsgId, hash: licenseHash,
        flow_type: activationFlow,
        license_context: licenseContext
      }, licenseDispatch)
      dispatch(setShowActionStatus(response))
      setTimeout(() => {
        dispatch(setShowActionStatus('NONE'))
      }, 5000)
    }
  }, [dispatch, activationFlow, globalTsgId, licenseContext, licenseHash, licenseDispatch])


  const columns = useMemo(() => [
    {
      accessor: 'primary_product_name',
      Header: 'primary_product_name',
    },
    {
      // accessor: 'product_desc',
      id: 'product_desc',
      Header: 'Name',
      canSort: false,
      Cell: ({ row, value }) => DataRenderer({ ...{ row, value, toggleClick } }),
      width: 225
    },
    {
      id: 'product_name',
      Header: 'Product',
      canSort: false,
      Cell: ({ row, value }) => ValueDataRenderer({ ...{ row, value, name: 'product_name' } }),
      width: 175
    },
    {
      id: 'status',
      Header: 'Status',
      Cell: ({ row, value }) => ValueDataRenderer({ ...{ row, value, name: 'status' } }),
      width: 85
    },
    {
      id: 'quantity',
      Header: 'Quantity',
      Cell: ({ row, value }) => ValueDataRenderer({ ...{ row, value, name: 'quantity' } }),
      width: 85
    },
    {
      accessor: 'contract',
      Header: 'Contract',
      // Cell: ({ row, value }) => ValueDataRenderer({ ...{ row, value, name: 'contract' } }),
      Cell: ({ row, value }) => _ContractRenderer({ ...{ row, value, name: 'contract' } }),
      width: 185
    },
    {
      accessor: 'claimed_by_tenant_name',
      Header: 'Claimed By Tenant',
      Cell: ({ row, value }) => ValueDataRenderer({ ...{ row, value, name: 'claimed_by_tenant_name' } }),
      width: 175
    },
    {
      id: 'associated_tenant',
      Header: 'Associated Tenant',
      Cell: ({ row, value }) => ValueDataRenderer({ ...{ row, value, name: 'associated_tenant' } })
    },
    {
      accessor: 'start_date',
      Header: 'Start Date',
      Cell: ({ row, value }) => ValueDataRenderer({ ...{ row, value, name: 'start_date' } }),
      width: 85
    },
    {
      accessor: 'end_date',
      Header: 'End Date',
      Cell: ({ row, value }) => ValueDataRenderer({ ...{ row, value, name: 'end_date' } }),
      width: 85
    },
    {
      accessor: 'is_primary',
      Header: 'is_primary'
    },
    {
      accessor: 'is_header',
      Header: 'is_header'
    },
    {
      accessor: 'start_date_search',
      Header: 'start_date_search'
    },
    {
      accessor: 'end_date_search',
      Header: 'end_date_search'
    },
    {
      accessor: 'license_id',
      Header: 'license_id'
    },
    {
      accessor: 'tsg_id',
      Header: 'tsg_id'
    },
    {
      accessor: 'isEval',
      Header: 'isEval'
    },
    {
      id: 'actions',
      Header: 'Actions',
      Cell: ({ value, row }) => <ButtonRenderer {...{ row, name: 'actions', value, dispatch, setUpdateLoading, setShowError }}
        title={CLAIMS_SUBSCRIPTION} handleSubmit={handleSubmit} setShowErrorMsg={setShowErrorMsg} {...props} />,
      width: 160,
    },
  ], [DataRenderer, toggleClick, ValueDataRenderer, _ContractRenderer, dispatch, handleSubmit, props])

  const updateErrorStatus = useCallback(() => {
    setShowError(false)
  }, [])

  /**
   *  Method calculated the total page numbers by filtering the header element.
   *  The page count is calculated based on the headers not with the sub records
   * @type {number[]}
   */
  const pageOptions = useMemo(() => {
    const headerFilter = tableData?.filter((each) => each.is_header)
    const length = Math.ceil(headerFilter?.length / currentPageSize)
    return length > 0 ? new Array(length).fill(null).map((_, index) => index) : []
  }, [currentPageSize, tableData])

  /*
   To extract the page data from the tableData using the headerId
   First filter the header elements to show the last element
   calculate the page first and last index and return the page data
   HeaderId is used to index the headers
   */
  const getData = useMemo(() => {
    if (!tableData) {
      return []
    }
    const headerFilter = tableData?.filter((each) => each.is_header)
    const totalPage = Math.ceil(headerFilter.length / currentPageSize)
    const _startIndex = Math.ceil(pageSize * currentPageIndex)
    const _endIndex = Math.ceil((pageSize * currentPageIndex) + pageSize)
    const startIndex = headerFilter[_startIndex]?.id
    const endIndex = (currentPageIndex + 1 === totalPage) ? tableData?.length : headerFilter[_endIndex]?.id
    return tableData?.filter((each, idx) => idx >= startIndex && idx < endIndex)
  }, [tableData, currentPageSize, currentPageIndex, pageSize])

  const getErrorBlockDisplay = useMemo(() => {
    if (showError || (rest?.licenseReducer?.licenseMessage && rest?.licenseReducer?.isLicenseError)) {
      return <MessageBanner
        message={showErrorMsg || rest?.licenseReducer?.licenseMessage}
        timerInMilliSec={5000}
        callBack={updateErrorStatus}
        donNotCloseBanner={rest?.licenseReducer?.isLicenseError}
      />
    }
    const messageInfo = rest?.licenseReducer?.licenseMessage.length ? rest?.licenseReducer?.licenseMessage :
      ((rest?.licenseReducer?.isPanorama ? PANORAMA_MULTI_CLOUD_USER_ALERT : ''))
    const closeBanner = rest?.licenseReducer?.licenseMessage ? false : rest?.licenseReducer?.isPanorama
    if (((rest?.licenseReducer?.licenseMessage && !rest?.licenseReducer?.isLicenseError) || rest?.licenseReducer?.isPanorama) && !showErrorMsg) {
      return <MessageBanner
        message={messageInfo}
        bannerProps={{
          type: 'page',
          appearance: 'standard',
          showIcon: true
        }}
        donNotCloseBanner={closeBanner} />
    }
  }, [rest?.licenseReducer?.isLicenseError, rest?.licenseReducer?.isPanorama, rest?.licenseReducer?.licenseMessage, showError, showErrorMsg, updateErrorStatus])

  const filterTableData = useCallback((event) => {
    const value = event.target.value
    const response = []
    let count = 0
    if (value?.trim().length < 1) {
      setTableData([...tableColumnData])
    }
    else {
      tableColumnData.forEach((each, index) => {
        if (each.tsg_id?.toLowerCase().indexOf(value.toLowerCase()) >= 0 || each.start_date_search?.toLowerCase().indexOf(value.toLowerCase()) >= 0 ||
          each.end_date_search?.toLowerCase().indexOf(value.toLowerCase()) >= 0 || each.contract?.toLowerCase().indexOf(value.toLowerCase()) >= 0 ||
          each.license_id?.toLowerCase().indexOf(value.toLowerCase()) >= 0 || each.claimed_by_tenant_name?.toLowerCase().indexOf(value.toLowerCase()) >= 0) {
          const data = { ...each, id: count }
          count++
          response.push(data)
        }
      })
      setTableData([...response])
    }
    setCurrentPageIndex(0)
  }, [tableColumnData])

  return (
    <div className={'subscription-table'}>
      {(isLoading && tableColumnData.length === 0) || rest?.newTsgLoading?.newTsg || updateLoading ? <LoadingPage /> : <>
        {getErrorBlockDisplay}
        <FadedLineHeader title={READY_FOR_ACTIVATION} />
        <TableStyle className="tw-flex tw-flex-col tw-h-full tw-border-gray-300 tw-w-full
        tw-rounded tw-border tw-px-3 tw--my-4 tw-py-4 tw-relative" >
          <TableProvider
            columns={columns}
            data={getData}
            enablePagination
            enableColumnSort
            initialState={{
              hiddenColumns: ['primary_product_name', 'end_date_search',
                'start_date_search', 'is_header', 'is_primary', 'license_id', 'tsg_id', 'isEval'], groupBy: ['primary_product_name']
            }}>
            <TableHeader title={APPROVED_SUBSCRIPTION_LABEL}>
              <Tooltip label={'Search For Table Content'}>
                <Input
                  aria-label="Table filter"
                  placeholder={'Search Table'}
                  onChange={filterTableData}
                  //value={searchValue}
                  prefix={<SearchIcon size='sm' className='cm-text-gray-300'/>}
                />
              </Tooltip>
            </TableHeader>

            <ConnectedTableLayout
              overflow
              paginator={<Paginator
                canPreviousPage={currentPageIndex > 0}
                canNextPage={currentPageIndex < pageOptions.length - 1}
                pageCount={pageOptions.length}
                pageIndex={currentPageIndex}
                pageOptions={pageOptions}
                pageSize={10}
                pageSizeOptions={[10]}
                onNextPage={() => {
                  //onPaginationChange({ pageIndex: pageIndex + 1, pageSize: 10 })
                  setCurrentPageIndex(currentPageIndex + 1)
                }}
                onPreviousPage={() => {
                  //onPaginationChange({ pageIndex: pageIndex - 1, pageSize: 10 })
                  setCurrentPageIndex(currentPageIndex - 1)
                }}
                onSelectPage={pageIndex => {
                  setCurrentPageIndex(pageIndex)
                }}
              />}
            >
              <ConnectedTable />
            </ConnectedTableLayout>
          </TableProvider>
        </TableStyle>
      </>
      }
    </div>
  )
}

export default SubscriptionTable

